import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md px-2 py-0.5 text-[10px] font-semibold w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 overflow-hidden",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary-500 text-primary-foreground hover:bg-primary-500/90",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        tertiary: "border-transparent bg-tertiary text-tertiary-foreground hover:bg-tertiary/80",
        destructive:
          "border-transparent bg-destructive text-white focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 hover:bg-destructive/90",
        outline: "text-foreground",
        success: "border-transparent bg-green-100 text-green-700 hover:bg-green-200",
        pending: "border-transparent bg-primary-100 text-primary-700 hover:bg-primary-200",
        error: "border-transparent bg-red-100 text-red-700 hover:bg-red-200",
        info: "border-transparent bg-blue-100 text-blue-700 hover:bg-blue-200",
        black: "border-transparent bg-neutral-100 text-neutral-700 hover:bg-neutral-200",
      },
    },
    defaultVariants: {  
      variant: "default",
    },
  }
)

function Badge({ className, variant, asChild = false, pill = false, ...props }: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean; pill?: boolean }) {
  const Comp = asChild ? Slot : "span"

  return <Comp data-slot="badge" className={cn(badgeVariants({ variant }), pill && "rounded-full", className)} {...props} />
}

export { Badge, badgeVariants }
